import LoadingAnimation from "@assets/svg/LoadingAnimation";
import CustomInput from "@components/CustomInput/CustomInput";
import {
  getCandidateProfilePdfData,
  updateCandidate,
} from "@endpoints/candidate.service";
import { RootState } from "@store/reducers";
import { candidateStatus } from "@utils/constants";
import { colors } from "@utils/theme";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { profileImage, uploadUrl } from "@endpoints/images.service";
import { pdf } from "@react-pdf/renderer";
import { Cropper } from "react-cropper";
// @ts-ignore
import "cropperjs/dist/cropper.css";
import { setUserData } from "@store/actions/auth.action";
import { Dialog, DialogClose, DialogContent } from "@components/ui/dialog";
import ResumePdfDocument from "@pages/ProfilePage/ResumePdfDocument";
import { X } from "lucide-react";

interface EditProfileModalProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
}

const availabilityStatus = Object.entries(candidateStatus).map(
  ([_, value]) => ({
    title: value,
    id: value,
  }),
);

const EditProfileModal = ({
  showModal,
  setShowModal,
}: EditProfileModalProps) => {
  const cropperRef = useRef(null);
  const filePickerRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const {
    watch,
    control,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      jobTitle: user?.jobTitle,
      status: availabilityStatus?.find((x) => x.id === user?.status)?.id ?? "",
      location: user?.location,
      image: undefined,
      openProfileModel: false,
      file: undefined,
      profileImage: undefined,
    },
  });

  const fields = watch();

  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const closeAndReset = () => {
    setShowModal(false);
    reset();
  };

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      setValue("file", file, { shouldDirty: true });
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setValue("image", reader.result, { shouldDirty: true });
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error("Error during file upload or API calls:", error);
    }
  };

  const getCroppedImage = (e) => {
    e.preventDefault();

    if (cropperRef.current) {
      const cropperInstance = cropperRef.current.cropper;
      if (cropperInstance) {
        const canvas = cropperInstance.getCroppedCanvas();
        if (!canvas) {
          enqueueSnackbar("Unable to crop image. Please try again.", {
            variant: "error",
          });
          return;
        }
        const croppedDataUrl = canvas.toDataURL();
        setValue("profileImage", croppedDataUrl);
        setValue("image", undefined);
      } else {
        console.error("Cropper instance is not found");
      }
    } else {
      console.error("cropperRef.current is not initialized");
    }
  };

  const cancelCrop = () => {
    setValue("image", undefined);
    setValue("file", undefined);
  };

  const updateProfile = async (formData) => {
    try {
      const payload = formData;
      if (!payload?.status) delete payload.status;
      setIsSavingProfile(true);
      if (payload?.profileImage) {
        const { data } = await profileImage();
        if (!data?.data || !data?.data.uploadURL) {
          throw new Error("Failed to get upload URL");
        }
        const formData = new FormData();
        const base64Data = payload?.profileImage?.split(";base64,").pop();
        const byteArray = Uint8Array.from(atob(base64Data), (c) =>
          c.charCodeAt(0),
        );
        const blob = new Blob([byteArray], { type: payload?.file?.type });
        formData.append("file", blob, payload?.file?.name);
        const fileResponse = await uploadUrl(data?.data.uploadURL, formData);
        delete payload["file"];
        delete payload["profileImage"];
        delete payload["image"];
        const picURl = fileResponse?.variants?.find(
          (x) => x.split("/").slice(-1)[0] === "public",
        );
        payload["profilePic"] = picURl;
      }
      const { success, data: userApiData } = await updateCandidate(payload);
      if (success) {
        dispatch(setUserData(userApiData));
        setValue("profileImage", undefined);
        setValue("image", undefined);
        setValue("openProfileModel", false);
      }
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
      console.log("error", error);
    } finally {
      closeAndReset();
      setIsSavingProfile(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      if (!user?._id) return;
      setIsDownloadingPdf(true);
      const { success, data } = await getCandidateProfilePdfData(user._id);
      if (!success) {
        throw new Error(data?.message || "Unable to generate resume PDF.");
      }
      const hasAnyContent =
        (data?.about && data.about.trim()) ||
        (Array.isArray(data?.skills) && data.skills.length > 0) ||
        (Array.isArray(data?.experience) && data.experience.length > 0) ||
        (Array.isArray(data?.education) && data.education.length > 0) ||
        (Array.isArray(data?.certifications) && data.certifications.length > 0);
      if (!hasAnyContent) {
        enqueueSnackbar(
          "Please add your profile details (About, Skills, Experience, Education, or Certifications) before generating the resume PDF.",
          { variant: "warning" },
        );
        return;
      }
      const blob = await pdf(<ResumePdfDocument data={data} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      const previewWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!previewWindow) {
        const link = document.createElement("a");
        const safeFirstName = user?.firstName
          ? user.firstName.trim()
          : "candidate";
        const safeLastName = user?.lastName ? user.lastName.trim() : "profile";
        link.href = url;
        link.setAttribute(
          "download",
          `${safeFirstName}-${safeLastName}-profile.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);
    } catch (error) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (!showModal) return;
    reset({
      jobTitle: user?.jobTitle,
      status: availabilityStatus?.find((x) => x.id === user?.status)?.id ?? "",
      location: user?.location,
    });
  }, [showModal, user]);

  return (
    <Dialog open={showModal} onOpenChange={(open) => !open && closeAndReset()}>
      <DialogContent showCloseButton={false} className="bg-white !max-w-2xl">
        <div
          className="flex flex-col bg-white"
          onClick={(e) => e.stopPropagation()} // Prevent modal from closing when clicking inside
        >
          <div className="flex flex-1 items-center justify-between pb-6">
            <span className="font-montserrat text-charcoal font-bold text-xl">
              Edit
            </span>
            <DialogClose asChild>
              <button
                onClick={closeAndReset}
                className="self-end text-black text-2xl font-semibold"
              >
                <X size={24} />
              </button>
            </DialogClose>
          </div>
          <div className="flex gap-10">
            <div className="flex flex-col flex-1 gap-6">
              <div className="flex self-center rounded-full h-40 w-40 bg-[#E1E0E0] items-center justify-center">
                {fields?.profileImage || user?.profilePic ? (
                  <img
                    src={fields?.profileImage || user?.profilePic}
                    className="rounded-full h-40 w-40 object-fill"
                  />
                ) : (
                  <span className="font-montserrat text-white font-semibold text-6xl">
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => filePickerRef?.current?.click()}
                className="flex  items-center border rounded-lg overflow-hidden pr-2"
              >
                <span className="text-sm text-[#6f6f6f] font-medium py-2.5 px-3 bg-[#E9EAF0]">
                  PNG/JPG
                </span>
                <span className="text-xs flex font-medium px-3 text-ngray flex-1 text-left whitespace-nowrap">
                  {fields?.file
                    ? fields?.file?.name
                    : user?.profilePic
                      ? "Change profile picture"
                      : "Upload your profile picture"}
                </span>
              </button>
              <input
                ref={filePickerRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
            <div className="flex flex-1 flex-col gap-5">
              {/* @ts-ignore */}
              <CustomInput
                label={"Job Title"}
                name="jobTitle"
                placeholder={"Ex: Project Manager"}
                control={control}
                errors={errors}
              />
              <Controller
                name="status"
                control={control}
                render={({ field }) => {
                  return (
                    <div className={`flex flex-col w-full`}>
                      <span className="font-montserrat text-charcoal font-medium text-xs mb-2">
                        Work Status
                      </span>
                      <div
                        className={`rounded-lg py-2.5 px-3 font-montserrat font-medium text-xs flex items-center border`}
                      >
                        <select
                          {...field}
                          className="w-full outline-none bg-transparent focus:outline-none focus:border-blue-500 text-charcoal"
                          name={"status"}
                          id={"status"}
                        >
                          <option value="" disabled hidden className={""}>
                            Are you available for job?
                          </option>
                          {availabilityStatus?.map((item) => {
                            return (
                              <option
                                className="text-black"
                                key={item?.id}
                                value={item?.id}
                              >
                                {item?.title}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      {errors["status"]?.message && (
                        <span className="whitespace-pre text-xs mt-2 text-orange-700">
                          {errors["status"]?.message}
                        </span>
                      )}
                    </div>
                  );
                }}
              />
              {/* @ts-ignore */}
              <CustomInput
                label={"Location"}
                name="location"
                placeholder={"Ex: United Kingdom"}
                control={control}
                errors={errors}
              />
            </div>
          </div>

          <div className="flex flex-col mt-6">
            {fields?.image && (
              <>
                <div className="edit-profile-cropper">
                  <Cropper
                    src={fields?.image}
                    ref={cropperRef}
                    style={{ height: 400, width: "100%" }}
                    aspectRatio={1}
                    viewMode={2}
                    guides={false}
                    minCropBoxWidth={50}
                    minCropBoxHeight={50}
                  />
                </div>
                <style>
                  {`
                    .edit-profile-cropper .cropper-face,
                    .edit-profile-cropper .cropper-view-box,
                    .edit-profile-cropper .cropper-crop-box {
                      border-radius: 50%;
                      border: 2px solid white;
                    }
                    .edit-profile-cropper .cropper-face {
                      background-color: rgba(0, 0, 0, 0.3);
                    }
                  `}
                </style>
              </>
            )}
            <div
              className={`${fields?.image ? "mt-6" : "mt-4"} flex justify-end gap-3`}
            >
              {fields?.image && (
                <button
                  type="button"
                  onClick={cancelCrop}
                  className="font-semibold text-sm border border-gray-300 py-2 px-5 text-charcoal rounded-md"
                >
                  Cancel
                </button>
              )}
              <button
                disabled={isSavingProfile}
                onClick={(e) => {
                  if (fields?.image) {
                    getCroppedImage(e);
                  } else {
                    handleSubmit(updateProfile)();
                  }
                }}
                className="flex gap-2 items-center font-semibold text-sm bg-primary py-2 px-5 text-white rounded-md"
              >
                {fields?.image ? "Crop Image" : "Save"}
                {isSavingProfile && (
                  <LoadingAnimation color={colors.White} className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;
